import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, resolveImg, formatApiError } from "../lib/api";
import { ALLERGEN_LABEL, ALLERGEN_ICON, TAG_MAP } from "../lib/menuConstants";
import { LANG_OPTIONS, tContent } from "../lib/i18n";
import { useCart } from "../context/CartContext";
import { useLang } from "../context/LanguageContext";
import CartSheet from "../components/CartSheet";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { ShoppingBag, Plus, Lock, Globe, ClockCounterClockwise } from "@phosphor-icons/react";

const HERO = "https://images.pexels.com/photos/19343364/pexels-photo-19343364.jpeg?auto=compress&cs=tinysrgb&w=1600";

function variantMin(item) {
  return item.variants?.length ? Math.min(...item.variants.map((v) => v.price)) : null;
}

function priceText(item, t) {
  const vm = variantMin(item);
  if (vm != null) return `${t("from")} ${vm.toFixed(2)} €`;
  if (item.price_label === "Consultar") return t("consult");
  return `${item.price.toFixed(2)} €${item.price_label ? ` ${item.price_label}` : ""}`;
}

export default function MenuPage() {
  const [settings, setSettings] = useState({
    bar_name: "Bar Sense Nom",
    tagline: "",
    address: "",
    phone: "",
    show_cart_total: true,
    default_language: "va",
  });
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("all");
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ customer_name: "", table_number: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const cart = useCart();
  const { lang, setLang, t } = useLang();

  useEffect(() => {
    Promise.all([api.get("/categories"), api.get("/menu-items"), api.get("/settings/bar")])
      .then(([c, m, s]) => {
        setCategories(c.data);
        setItems(m.data);
        setSettings(s.data);
        if (!localStorage.getItem("fichaje_carta_lang") && s.data.default_language) {
          setLang(s.data.default_language);
        }
      })
      .catch((e) => toast.error(formatApiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const topLevels = useMemo(
    () => categories.filter((c) => !c.parent_id).sort((a, b) => a.order - b.order),
    [categories]
  );
  const childrenOf = (id) =>
    categories.filter((c) => c.parent_id === id).sort((a, b) => a.order - b.order);
  const availItems = useMemo(() => items.filter((i) => i.available), [items]);
  const itemsOf = (catId) =>
    availItems
      .filter((i) => i.category_id === catId)
      .sort((a, b) => {
        const at = (a.tags?.length || 0) > 0 ? 0 : 1;
        const bt = (b.tags?.length || 0) > 0 ? 0 : 1;
        if (at !== bt) return at - bt;
        return a.order - b.order;
      });
  const visibleFamilies = active === "all" ? topLevels : topLevels.filter((f) => f.id === active);

  const openItem = (item) => {
    if (item.variants?.length) {
      setDetail(item);
      return;
    }
    cart.add(item);
  };

  const submitOrder = async () => {
    if (!form.customer_name.trim()) {
      toast.error(t("enter_name"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = cart.items.map((i) => ({
        item_id: i.item_id,
        name: i.name,
        price: i.price,
        quantity: i.quantity,
        variant: i.variant,
      }));
      await api.post("/orders", { ...form, items: payload });
      toast.success(t("order_success"));
      cart.clear();
      setForm({ customer_name: "", table_number: "", notes: "" });
      setCheckoutOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="carta-scope min-h-screen bg-background text-foreground">
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-3">
          <span
            className="font-serif text-2xl tracking-tight text-foreground truncate"
            data-testid="brand"
          >
            {settings.bar_name}
          </span>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Globe size={16} className="hidden sm:block" />
              <Select value={lang} onValueChange={setLang}>
                <SelectTrigger
                  className="h-9 w-[110px] bg-transparent border-border text-xs"
                  data-testid="lang-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANG_OPTIONS.map((l) => (
                    <SelectItem key={l.code} value={l.code} data-testid={`lang-${l.code}`}>
                      {l.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Link
              to="/fichaje"
              className="text-muted-foreground hover:text-primary transition-colors p-2"
              data-testid="fichaje-link"
              title="Fichaje personal"
            >
              <ClockCounterClockwise size={18} weight="duotone" />
            </Link>
            <Link
              to="/admin/login"
              className="text-muted-foreground hover:text-primary transition-colors p-2"
              data-testid="admin-link"
              title={t("admin")}
            >
              <Lock size={16} />
            </Link>
            <button
              onClick={() => cart.setOpen(true)}
              className="relative flex items-center gap-2 border border-border rounded-sm px-3 py-2 hover:border-primary transition-colors"
              data-testid="open-cart-btn"
            >
              <ShoppingBag size={16} />
              <span className="text-sm" data-testid="cart-count">
                {cart.count}
              </span>
            </button>
          </div>
        </div>
      </header>

      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <img src={HERO} alt="Interior" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative text-center px-6 animate-fade-up">
          <p className="text-primary uppercase tracking-widest text-xs mb-4">{t("hero_kicker")}</p>
          <h1 className="font-serif font-light text-5xl md:text-7xl text-foreground leading-none">
            {settings.bar_name}
          </h1>
          {settings.tagline && (
            <p className="text-muted-foreground mt-5 max-w-md mx-auto font-light">
              {settings.tagline}
            </p>
          )}
        </div>
      </section>

      <div className="sticky top-16 z-30 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-4 flex gap-2 overflow-x-auto">
          <FilterChip
            label={t("all")}
            active={active === "all"}
            onClick={() => setActive("all")}
            testid="filter-all"
          />
          {topLevels.map((c) => (
            <FilterChip
              key={c.id}
              label={tContent(c, lang).name}
              active={active === c.id}
              onClick={() => setActive(c.id)}
              testid={`filter-${c.id}`}
            />
          ))}
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-6 py-16">
        {visibleFamilies.map((family) => {
          const kids = childrenOf(family.id);
          const directItems = itemsOf(family.id);
          const hasContent = kids.some((k) => itemsOf(k.id).length) || directItems.length;
          if (!hasContent) return null;
          return (
            <section key={family.id} className="mb-24" data-testid={`section-${family.id}`}>
              <div className="flex items-center gap-4 mb-10">
                <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground">
                  {tContent(family, lang).name}
                </h2>
                <div className="flex-1 h-px bg-border" />
              </div>
              {directItems.length > 0 && (
                <ItemGrid
                  items={directItems}
                  lang={lang}
                  t={t}
                  onOpen={openItem}
                  onDetail={setDetail}
                />
              )}
              {kids.map((kid) => {
                const kidItems = itemsOf(kid.id);
                if (!kidItems.length) return null;
                return (
                  <div key={kid.id} className="mb-14" data-testid={`subsection-${kid.id}`}>
                    <h3 className="uppercase tracking-widest text-primary text-sm mb-8 mt-4">
                      {tContent(kid, lang).name}
                    </h3>
                    <ItemGrid
                      items={kidItems}
                      lang={lang}
                      t={t}
                      onOpen={openItem}
                      onDetail={setDetail}
                    />
                  </div>
                );
              })}
              <p
                className="mt-8 pt-6 border-t border-border/50 text-xs text-muted-foreground italic leading-relaxed"
                data-testid={`allergen-notice-${family.id}`}
              >
                {t("allergen_notice")}
              </p>
              <p
                className="mt-2 text-xs text-muted-foreground italic leading-relaxed"
                data-testid={`photos-notice-${family.id}`}
              >
                {t("photos_notice")}
              </p>
            </section>
          );
        })}
      </main>

      <footer className="border-t border-border py-10 text-center text-muted-foreground text-sm">
        <div>
          {settings.bar_name}
          {settings.address ? ` · ${settings.address}` : ""}
          {settings.phone ? ` · ${settings.phone}` : ""}
        </div>
        <div className="mt-3">
          <Link
            to="/carta/alergenos"
            className="text-xs uppercase tracking-widest text-primary hover:underline"
            data-testid="link-allergens-page"
          >
            {lang === "es" ? "Consultar información de alérgenos" :
             lang === "en" ? "View allergen information" :
             lang === "fr" ? "Consulter les allergènes" :
             "Consultar informació d'al·lèrgens"}
          </Link>
        </div>
      </footer>

      <CartSheet onCheckout={() => setCheckoutOpen(true)} showPrices={settings.show_cart_total} />

      {/* Detail modal */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent
          className="carta-scope bg-card border border-border sm:max-w-lg max-h-[92vh] overflow-y-auto p-0 text-foreground"
          data-testid="item-detail-dialog"
        >
          {detail && (
            <ItemDetail
              item={detail}
              lang={lang}
              t={t}
              onAdd={(variant) => {
                cart.add(detail, variant);
                setDetail(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Checkout */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent
          className="carta-scope bg-card border border-border sm:max-w-md text-foreground"
          data-testid="checkout-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl font-light">
              {t("checkout_title")}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {settings.show_cart_total && (
                <>
                  <span className="text-primary">{cart.total.toFixed(2)} €</span> ·{" "}
                </>
              )}
              {cart.count} {t("articles")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                {t("name")} *
              </Label>
              <Input
                value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                className="mt-2 bg-transparent border-white/20 focus:border-primary"
                placeholder={t("name_ph")}
                data-testid="checkout-name"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                {t("table")}
              </Label>
              <Input
                value={form.table_number}
                onChange={(e) => setForm({ ...form, table_number: e.target.value })}
                className="mt-2 bg-transparent border-white/20 focus:border-primary"
                placeholder={t("table_ph")}
                data-testid="checkout-table"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                {t("notes")}
              </Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-2 bg-transparent border-white/20 focus:border-primary"
                placeholder={t("notes_ph")}
                data-testid="checkout-notes"
              />
            </div>
            <Button
              className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 h-12 tracking-widest text-xs uppercase"
              onClick={submitOrder}
              disabled={submitting}
              data-testid="confirm-order-btn"
            >
              {submitting ? t("sending") : t("confirm_order")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ItemGrid({ items, lang, t, onOpen, onDetail }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
      {items.map((item, idx) => (
        <MenuCard
          key={item.id}
          item={item}
          lang={lang}
          t={t}
          onOpen={() => onOpen(item)}
          onDetail={() => onDetail(item)}
          delay={idx * 40}
        />
      ))}
    </div>
  );
}

function FilterChip({ label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`whitespace-nowrap px-5 py-2 rounded-full text-xs uppercase tracking-widest transition-colors border ${
        active
          ? "bg-primary text-primary-foreground border-primary"
          : "border-border text-muted-foreground hover:text-foreground hover:border-white/30"
      }`}
    >
      {label}
    </button>
  );
}

function MenuCard({ item, lang, t, onOpen, onDetail, delay }) {
  const c = tContent(item, lang);
  return (
    <div
      className="group animate-fade-up"
      style={{ animationDelay: `${delay}ms` }}
      data-testid={`menu-item-${item.id}`}
    >
      <div
        className="relative overflow-hidden rounded-sm aspect-[4/3] bg-secondary cursor-pointer"
        onClick={onDetail}
        data-testid={`open-detail-${item.id}`}
      >
        {item.image_url && (
          <img
            src={resolveImg(item.image_url)}
            alt={c.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
          />
        )}
        {item.tags?.length > 0 && (
          <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
            {item.tags.map(
              (tg) =>
                TAG_MAP[tg] && (
                  <span
                    key={tg}
                    className={`text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border ${TAG_MAP[tg].className}`}
                    data-testid={`tag-${item.id}-${tg}`}
                  >
                    {TAG_MAP[tg].label}
                  </span>
                )
            )}
          </div>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          className="absolute bottom-3 right-3 w-11 h-11 flex items-center justify-center bg-primary text-primary-foreground rounded-full shadow-lg opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 hover:bg-primary/90"
          data-testid={`add-to-cart-${item.id}`}
          aria-label={t("add_to_order")}
        >
          <Plus size={18} weight="bold" />
        </button>
      </div>
      <div className="flex items-baseline justify-between gap-3 mt-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h4
              className="font-serif text-2xl font-light text-foreground leading-tight cursor-pointer hover:text-primary transition-colors"
              onClick={onDetail}
            >
              {c.name}
            </h4>
            {item.allergens?.length > 0 && (
              <span
                className="inline-flex items-center gap-0.5 text-base leading-none"
                data-testid={`allergens-inline-${item.id}`}
                title={item.allergens.map((a) => ALLERGEN_LABEL[a] || a).join(" · ")}
              >
                {item.allergens.map((a) => (
                  <span
                    key={a}
                    className="opacity-80"
                    aria-label={ALLERGEN_LABEL[a] || a}
                  >
                    {ALLERGEN_ICON[a] || "•"}
                  </span>
                ))}
              </span>
            )}
          </div>
        </div>
        <span className="text-primary font-medium whitespace-nowrap text-sm">
          {priceText(item, t)}
        </span>
      </div>
      {c.description && (
        <p className="text-muted-foreground text-sm mt-1.5 font-light leading-relaxed">
          {c.description}
        </p>
      )}
    </div>
  );
}

function ItemDetail({ item, lang, t, onAdd }) {
  const c = tContent(item, lang);
  const [variant, setVariant] = useState(item.variants?.[0]?.name || "");
  const selected = item.variants?.find((v) => v.name === variant) || null;
  return (
    <div>
      {item.image_url && (
        <div className="relative aspect-[16/10] w-full overflow-hidden rounded-t-lg">
          <img
            src={resolveImg(item.image_url)}
            alt={c.name}
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <div className="p-6">
        <DialogHeader>
          <DialogTitle className="font-serif text-4xl font-light text-left">{c.name}</DialogTitle>
          {c.description && (
            <DialogDescription className="text-muted-foreground text-left text-base pt-1">
              {c.description}
            </DialogDescription>
          )}
        </DialogHeader>

        {item.allergens?.length > 0 && (
          <div className="mt-5" data-testid="detail-allergens">
            <p className="uppercase tracking-widest text-primary/80 text-xs mb-2">
              {t("allergens")}
            </p>
            <div className="flex flex-wrap gap-2">
              {item.allergens.map((a) => (
                <span
                  key={a}
                  className="text-xs px-3 py-1 rounded-full border border-border text-muted-foreground"
                >
                  {ALLERGEN_LABEL[a] || a}
                </span>
              ))}
            </div>
          </div>
        )}

        {item.variants?.length > 0 ? (
          <div className="mt-6">
            <p className="uppercase tracking-widest text-primary/80 text-xs mb-3">
              {t("choose_size")}
            </p>
            <div className="grid grid-cols-2 gap-3">
              {item.variants.map((v) => (
                <button
                  key={v.name}
                  onClick={() => setVariant(v.name)}
                  data-testid={`variant-${v.name}`}
                  className={`flex items-center justify-between px-4 py-3 rounded-sm border transition-colors ${
                    variant === v.name
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border text-muted-foreground hover:border-white/40"
                  }`}
                >
                  <span>{v.name}</span>
                  <span className="text-primary text-sm">{v.price.toFixed(2)} €</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <p className="mt-5 text-primary font-serif text-2xl">{priceText(item, t)}</p>
        )}

        <Button
          className="w-full mt-7 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 h-12 tracking-widest text-xs uppercase"
          data-testid="detail-add-btn"
          onClick={() => onAdd(selected)}
        >
          {t("add_to_order")}
          {selected
            ? ` · ${selected.price.toFixed(2)} €`
            : item.price_label !== "Consultar" && !item.variants?.length
            ? ` · ${item.price.toFixed(2)} €`
            : ""}
        </Button>
      </div>
    </div>
  );
}
